import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { form, FormField, required } from '@angular/forms/signals';
import { EMPTY, map, Subject, Subscription } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import {
  AppSvgIconComponent,
  SignalButton,
  SignalFormDirective,
  SignalTextField,
  State,
  validateSignalForm,
} from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import {
  PublicFormsApiService,
  type PublicFormAccessState,
  type PublicFormAuthOptions,
  type PublicFormVisibility,
  type ResolvePublicFormApiResponse,
  type UnlockPublicFormApiResponse,
} from '@core/api/public/public-forms-api.service';
import { SubmissionUploadApiService } from '@core/api/storage/submission-upload-api.service';
import { FormAccessTokenStorageService } from '@core/services/form-access-token-storage.service';
import { FormDraftKeyStorageService } from '@core/services/form-draft-key-storage.service';
import { RespondentTokenStorageService } from '@core/services/respondent-token-storage.service';
import { RespondentAccountMenuButtonComponent } from '../../../../my-forms/components/respondent-account-menu-button/respondent-account-menu-button';
import {
  FormPreviewCardComponent,
  type PublicFormFillContext,
} from '../../components/form-preview-card/form-preview-card.component';
import type { BuiltForm } from '../../models/built-form';
import { mapBuiltFormDtoToBuiltForm } from '../../utils/map-built-form-dto';
import {
  applyFileIdsToAnswers,
  collectPendingFiles,
  serializeAnswersForApi,
} from '../../utils/serialize-answers-for-api';
import type { RespondentAnswers } from '../../utils/respondent-answer-form';

interface PasswordModel {
  password: string;
}

type PageView =
  | 'loading'
  | 'redirecting'
  | 'form'
  | 'password'
  | 'not_yet_available'
  | 'closed'
  | 'forbidden'
  | 'not_found'
  | 'success'
  | 'limit_reached'
  | 'error';

@Component({
  selector: 'app-public-form-page',
  standalone: true,
  imports: [
    AppSvgIconComponent,
    DatePipe,
    FormField,
    FormPreviewCardComponent,
    RespondentAccountMenuButtonComponent,
    RouterLink,
    SignalButton,
    SignalFormDirective,
    SignalTextField,
  ],
  templateUrl: './public-form-page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex min-h-dvh w-full flex-col bg-slate-50 font-inter',
  },
})
export class PublicFormPage extends BaseComponent implements OnInit, OnDestroy {
  private readonly publicFormsApi = inject(PublicFormsApiService);
  private readonly uploadApi = inject(SubmissionUploadApiService);
  private readonly respondentTokens = inject(RespondentTokenStorageService);
  private readonly formAccessTokens = inject(FormAccessTokenStorageService);
  private readonly draftKeys = inject(FormDraftKeyStorageService);

  private readonly previewCard =
    viewChild<FormPreviewCardComponent>(FormPreviewCardComponent);

  readonly workspaceSlug = toSignal(
    this.route.paramMap.pipe(
      map((params) => params.get('workspaceSlug')?.trim().toLowerCase() ?? ''),
    ),
    {
      initialValue:
        this.route.snapshot.paramMap
          .get('workspaceSlug')
          ?.trim()
          .toLowerCase() ?? '',
    },
  );

  readonly formSlug = toSignal(
    this.route.paramMap.pipe(
      map((params) => params.get('formSlug')?.trim().toLowerCase() ?? ''),
    ),
    {
      initialValue:
        this.route.snapshot.paramMap.get('formSlug')?.trim().toLowerCase() ??
        '',
    },
  );

  readonly resolveState = new State<ResolvePublicFormApiResponse>();
  readonly unlockState = new State<UnlockPublicFormApiResponse>();

  readonly view = signal<PageView>('loading');
  readonly formName = signal<string | null>(null);
  readonly visibility = signal<PublicFormVisibility | null>(null);
  readonly availableAt = signal<string | null>(null);
  readonly closedAt = signal<string | null>(null);
  readonly form = signal<BuiltForm | null>(null);
  readonly formId = signal<string | null>(null);
  readonly formVersion = signal<number | null>(null);
  readonly requireApproval = signal(false);
  /** When true (publish retentionHours === 0), never get/put drafts. */
  readonly draftsDisabled = signal(false);
  readonly draftAnswers = signal<Array<{ fieldId: string; value: unknown }> | null>(
    null,
  );
  readonly submissionId = signal<string | null>(null);
  readonly submissionReference = signal<string | null>(null);
  readonly submitting = signal(false);
  readonly draftKey = signal<string | null>(null);
  readonly submitError = signal<string | null>(null);

  readonly passwordModel = signal<PasswordModel>({ password: '' });
  readonly passwordForm = form(this.passwordModel, (schema) => {
    required(schema.password, { message: 'Enter the form password' });
  });

  readonly title = computed(() => this.formName() ?? 'Form');

  readonly publicContext = computed((): PublicFormFillContext | null => {
    const ws = this.workspaceSlug();
    const fs = this.formSlug();
    const key = this.draftKey();
    if (!ws || !fs || !key) {
      return null;
    }
    return {
      workspaceSlug: ws,
      formSlug: fs,
      auth: this.authOptions(),
    };
  });

  /** Bumped when form-access / respondent session storage changes so chrome computeds refresh. */
  private readonly chromeEpoch = signal(0);

  readonly showWorkspaceChrome = computed(() => {
    this.chromeEpoch();
    const view = this.view();
    if (view === 'loading' || view === 'redirecting') {
      return false;
    }
    return !!this.respondentTokens.getAccessToken();
  });

  readonly showPasswordChrome = computed(() => {
    this.chromeEpoch();
    if (this.showWorkspaceChrome()) {
      return false;
    }
    const ws = this.workspaceSlug();
    const fs = this.formSlug();
    if (!ws || !fs) {
      return false;
    }
    const view = this.view();
    if (view === 'loading' || view === 'redirecting' || view === 'password') {
      return false;
    }
    return !!this.formAccessTokens.get(ws, fs);
  });

  readonly showPublicChrome = computed(() => {
    if (this.showWorkspaceChrome() || this.showPasswordChrome()) {
      return false;
    }
    if (this.visibility() !== 'public') {
      return false;
    }
    const view = this.view();
    return (
      view !== 'loading' && view !== 'redirecting' && view !== 'password'
    );
  });

  readonly showChrome = computed(
    () =>
      this.showWorkspaceChrome() ||
      this.showPasswordChrome() ||
      this.showPublicChrome(),
  );

  private redirectingToAuth = false;
  private readonly draftSave$ = new Subject<RespondentAnswers>();
  private draftSaveSub: Subscription | null = null;

  ngOnInit(): void {
    this.draftSaveSub = this.draftSave$
      .pipe(
        debounceTime(1200),
        switchMap((answers) => {
          const ws = this.workspaceSlug();
          const fs = this.formSlug();
          const built = this.form();
          const version = this.formVersion();
          if (!ws || !fs || !built || version == null) {
            return EMPTY;
          }
          const payload = serializeAnswersForApi(built, answers);
          return this.publicFormsApi.putDraft(
            ws,
            fs,
            { answers: payload, formVersion: version },
            this.authOptions(),
          );
        }),
      )
      .subscribe({
        next: (response) => {
          if (response?.data?.submissionId) {
            this.submissionId.set(response.data.submissionId);
          }
        },
        error: () => {
          // Keep editing; next change retries.
        },
      });

    this.loadForm();
  }

  ngOnDestroy(): void {
    this.draftSaveSub?.unsubscribe();
  }

  onUnlock(): void {
    if (!validateSignalForm(this.passwordForm)) {
      return;
    }
    const workspaceSlug = this.workspaceSlug();
    const formSlug = this.formSlug();
    if (!workspaceSlug || !formSlug) {
      return;
    }

    this.executeRequest({
      state: this.unlockState,
      request: this.publicFormsApi.unlockPublicForm(
        workspaceSlug,
        formSlug,
        this.passwordModel().password,
      ),
      showLoader: true,
      handleError: true,
      errorMessage: 'Could not unlock this form',
      onSuccess: (response) => {
        this.formAccessTokens.set(
          workspaceSlug,
          formSlug,
          response.data.accessToken,
        );
        this.chromeEpoch.update((n) => n + 1);
        this.passwordModel.set({ password: '' });
        this.loadForm();
      },
    });
  }

  leavePasswordSession(): void {
    const workspaceSlug = this.workspaceSlug();
    const formSlug = this.formSlug();
    if (!workspaceSlug || !formSlug) {
      return;
    }
    this.formAccessTokens.clear(workspaceSlug, formSlug);
    this.chromeEpoch.update((n) => n + 1);
    this.form.set(null);
    this.loadForm();
  }

  closePublicForm(): void {
    window.close();
    void this.router.navigate(['/']);
  }

  onAnswersChanged(answers: RespondentAnswers): void {
    if (this.view() !== 'form' || this.submitting() || this.draftsDisabled()) {
      return;
    }
    this.draftSave$.next(answers);
  }

  async onSubmitAnswers(answers: RespondentAnswers): Promise<void> {
    const workspaceSlug = this.workspaceSlug();
    const formSlug = this.formSlug();
    const built = this.form();
    const formId = this.formId();
    if (!workspaceSlug || !formSlug || !built || !formId) {
      return;
    }

    this.submitting.set(true);
    this.submitError.set(null);

    try {
      let working = { ...answers };
      let submissionId = this.draftsDisabled() ? null : this.submissionId();

      // When drafts are enabled, ensure a draft exists before file uploads.
      if (!this.draftsDisabled() && !submissionId) {
        const draftRes = await new Promise<{
          data: { submissionId: string };
        }>((resolve, reject) => {
          this.publicFormsApi
            .putDraft(
              workspaceSlug,
              formSlug,
              {
                answers: serializeAnswersForApi(built, working),
                formVersion: this.formVersion() ?? undefined,
              },
              this.authOptions(),
            )
            .subscribe({ next: resolve, error: reject });
        });
        submissionId = draftRes.data.submissionId;
        this.submissionId.set(submissionId);
      }

      const pending = collectPendingFiles(built, working);
      for (const group of pending) {
        const fileIds: string[] = [];
        for (const file of group.files) {
          const fileId = await this.uploadApi.uploadSubmissionFile({
            file,
            formId,
            fieldId: group.fieldId,
            submissionId,
            auth: this.authOptions(),
          });
          fileIds.push(fileId);
        }
        working = applyFileIdsToAnswers(working, group.fieldId, fileIds);
      }

      this.previewCard()?.patchAnswers(working);

      const result = await new Promise<{
        data: { submissionId: string; status: string; reference: string };
      }>((resolve, reject) => {
        this.publicFormsApi
          .createSubmission(
            workspaceSlug,
            formSlug,
            {
              answers: serializeAnswersForApi(built, working),
              ...(submissionId ? { submissionId } : {}),
            },
            this.authOptions(),
          )
          .subscribe({ next: resolve, error: reject });
      });

      this.submissionId.set(result.data.submissionId);
      this.submissionReference.set(
        result.data.reference?.trim() || null,
      );
      if (!this.draftsDisabled()) {
        this.draftKeys.rotate(workspaceSlug, formSlug);
      }
      this.view.set('success');
    } catch (error: unknown) {
      const status = (error as { status?: number })?.status;
      const body = (error as { error?: { message?: string; code?: string } })
        ?.error;
      if (
        status === 403 &&
        (body?.code === 'SUBMISSION_LIMIT_REACHED' ||
          body?.code === 'FORM_MAX_SUBMISSIONS_REACHED')
      ) {
        this.view.set('limit_reached');
        return;
      }
      this.submitError.set(
        typeof body?.message === 'string'
          ? body.message
          : 'Could not submit this form. Please try again.',
      );
    } finally {
      this.submitting.set(false);
    }
  }

  private authOptions(): PublicFormAuthOptions {
    const ws = this.workspaceSlug();
    const fs = this.formSlug();
    return {
      respondentAccessToken: this.respondentTokens.getAccessToken(),
      formAccessToken: ws && fs ? this.formAccessTokens.get(ws, fs) : null,
      draftKey: this.draftKey(),
    };
  }

  private loadForm(): void {
    if (this.redirectingToAuth || this.view() === 'redirecting') {
      return;
    }

    const workspaceSlug = this.workspaceSlug();
    const formSlug = this.formSlug();
    if (!workspaceSlug || !formSlug) {
      this.view.set('not_found');
      return;
    }

    const key = this.draftKeys.getOrCreate(workspaceSlug, formSlug);
    this.draftKey.set(key);

    this.view.set('loading');
    this.executeRequest({
      state: this.resolveState,
      request: this.publicFormsApi.resolvePublicForm(workspaceSlug, formSlug, {
        respondentAccessToken: this.respondentTokens.getAccessToken(),
        formAccessToken: this.formAccessTokens.get(workspaceSlug, formSlug),
      }),
      handleError: false,
      onSuccess: (response) => {
        void this.applyAccess(response.data.access, response.data);
      },
      onFailed: (error) => {
        const status = error?.status;
        if (status === 404) {
          this.view.set('not_found');
          return;
        }
        if (status === 403) {
          this.view.set('forbidden');
          return;
        }
        this.view.set('error');
      },
    });
  }

  private async applyAccess(
    access: PublicFormAccessState,
    data: ResolvePublicFormApiResponse['data'],
  ): Promise<void> {
    this.formName.set(data.name ?? null);
    this.visibility.set(data.visibility ?? null);
    this.availableAt.set(data.availableAt ?? null);
    this.closedAt.set(data.closedAt ?? null);

    switch (access) {
      case 'granted': {
        if (!data.definition) {
          this.view.set('error');
          return;
        }
        this.formId.set(data.formId ?? null);
        this.formVersion.set(data.formVersion ?? null);
        this.requireApproval.set(!!data.requireSubmissionApproval);
        this.draftsDisabled.set(data.formSubmissionDraftRetentionHours === 0);
        this.form.set(mapBuiltFormDtoToBuiltForm(data.definition));

        if (!this.draftsDisabled()) {
          await this.loadServerDraft();
        } else {
          this.draftAnswers.set(null);
          this.submissionId.set(null);
        }
        this.view.set('form');
        return;
      }
      case 'password_required':
        this.view.set('password');
        return;
      case 'auth_required': {
        if (this.redirectingToAuth) {
          return;
        }
        this.redirectingToAuth = true;
        if (this.respondentTokens.getAccessToken()) {
          this.respondentTokens.clearSession();
        }
        this.view.set('redirecting');
        const returnUrl = `/${this.workspaceSlug()}/${this.formSlug()}`;
        void this.router.navigate(['/my-forms'], {
          queryParams: { returnUrl },
          replaceUrl: true,
        });
        return;
      }
      case 'not_yet_available':
        this.view.set('not_yet_available');
        return;
      case 'closed':
        this.view.set('closed');
        return;
      default:
        this.view.set('error');
    }
  }

  private loadServerDraft(): Promise<void> {
    const workspaceSlug = this.workspaceSlug();
    const formSlug = this.formSlug();
    if (!workspaceSlug || !formSlug) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.publicFormsApi
        .getDraft(workspaceSlug, formSlug, this.authOptions())
        .subscribe({
          next: (response) => {
            const draft = response.data?.draft;
            const currentVersion = this.formVersion();
            if (
              draft &&
              currentVersion != null &&
              draft.formVersion === currentVersion
            ) {
              this.submissionId.set(draft.submissionId);
              this.draftAnswers.set(draft.answers ?? []);
            } else {
              this.draftAnswers.set(null);
              this.submissionId.set(null);
            }
            resolve();
          },
          error: () => {
            this.draftAnswers.set(null);
            resolve();
          },
        });
    });
  }
}
